import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import errText from '../../lib/errText';
import axios from 'axios';
import { Trophy, Radio, Users, X, Crown, ArrowLeft, CalendarDays, BarChart3, GitBranch, List, ArrowDown, Info } from 'lucide-react';
import { fmtDate } from '../utils/dates';
import TournamentBracket, { MatchListView, ColumnsView, groupRounds, buildDropMap } from '../components/TournamentBracket';
import { buildLogoMap, groupByDivision, LogoLightbox, useLogoLightbox } from '../components/TeamLogo';
import StandingsTable from '../components/StandingsTable';
import RankingPanel from '../components/RankingPanel';
import DivisionPodium, { computePodium } from '../components/WinnersPodium';
import { THEMES } from '../themes';
import { useBackdropClose } from '../utils/backdropClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RED = '#E63946';
const BLUE = '#2563EB';

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8, background: '#0A0A0A',
  border: '1px solid #2A2A2A', color: '#fff', fontSize: 14, outline: 'none',
};

function RegisterModal({ slug, tid, tournament, gcash, payments, onClose, onDone }) {
  const backdrop = useBackdropClose(onClose);
  const isSingles = tournament.game_mode === 'singles';
  const size = isSingles ? 1 : (tournament.team_size || 2);
  const fee = Number(tournament.entry_fee || 0);
  const opts = payments?.options || ['full', 'deposit50', 'counter'];
  const canDeposit = opts.includes('deposit50');
  const canVenue = opts.includes('counter') || !gcash; // never leave a dead-end
  const canPayMongo = !!payments?.paymongo_enabled && fee > 0;
  const [names, setNames] = useState(Array(size).fill(''));
  const [teamName, setTeamName] = useState(''); const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(''); const [divId, setDivId] = useState('');
  const [payMethod, setPayMethod] = useState(
    fee > 0 ? (canPayMongo ? 'paymongo' : (gcash ? 'gcash' : 'venue')) : 'free');
  const [amountChoice, setAmountChoice] = useState('full');
  const [proofKey, setProofKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [logoKey, setLogoKey] = useState(null);
  const [logoThumbKey, setLogoThumbKey] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const setName = (i, v) => setNames(arr => arr.map((x, idx) => idx === i ? v : x));

  const uploadProof = async (file) => {
    if (!file) return;
    setUploading(true); setErr('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/sports/public/${slug}/tournaments/${tid}/upload-proof`, fd);
      setProofKey(r.data.proof_key);
    } catch { setErr('Proof upload failed'); }
    finally { setUploading(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true); setErr('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/sports/public/${slug}/tournaments/${tid}/upload-logo`, fd);
      setLogoKey(r.data.logo_key); setLogoThumbKey(r.data.logo_thumb_key);
      setLogoPreview(URL.createObjectURL(file));
    } catch { setErr('Logo upload failed — try a PNG or JPG image'); }
    finally { setLogoUploading(false); }
  };

  const submit = async () => {
    const players = names.map(n => n.trim()).filter(Boolean).map(n => ({ name: n, email: email.trim(), phone: phone.trim() }));
    if (players.length === 0) { setErr('Player 1 name is required'); return; }
    if (tournament.divisions?.length > 0 && !divId) { setErr('Please select a division'); return; }
    if (fee > 0 && payMethod === 'gcash' && !proofKey) { setErr('Please upload your GCash payment proof'); return; }
    setSaving(true); setErr('');
    try {
      const r = await axios.post(`${API}/sports/public/${slug}/tournaments/${tid}/register`, {
        team_name: teamName.trim(), players, division_id: divId || null,
        payment_method: fee > 0 ? payMethod : 'free', proof_key: proofKey,
        amount_choice: amountChoice,
        logo_key: logoKey, logo_thumb_key: logoThumbKey,
      });
      // For PayMongo: immediately create checkout session and redirect
      if (fee > 0 && payMethod === 'paymongo' && r.data?.team_id) {
        try {
          const c = await axios.post(
            `${API}/sports/public/${slug}/tournaments/${tid}/teams/${r.data.team_id}/pay/checkout`,
            { amount_choice: amountChoice });
          if (c.data?.checkout_url) {
            window.location.href = c.data.checkout_url;
            return;
          }
        } catch (e2) {
          const d2 = e2.response?.data?.detail;
          setErr(typeof d2 === 'object' ? d2.message : (d2 || 'Could not start online payment'));
          setSaving(false);
          return;
        }
      }
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === 'object' ? d.message : (d || 'Registration failed'));
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)' }} {...backdrop}>
      <div data-testid="register-modal" className="w-full max-w-md rounded-2xl p-5 max-h-[90vh] overflow-y-auto" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>REGISTER YOUR TEAM</h3>
          <button onClick={onClose} style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          {names.map((v, i) => (
            <input key={i} data-testid={`reg-player${i + 1}`} value={v} onChange={e => setName(i, e.target.value)} placeholder={`Player ${i + 1}${i === 0 ? ' *' : ''}`} style={inputStyle} />
          ))}
          <input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Team name (optional)" style={inputStyle} />
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)" style={inputStyle} />
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone (optional)" style={inputStyle} />
          {tournament.divisions?.length > 0 && (
            <select data-testid="reg-division" value={divId} onChange={e => setDivId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">Select division *</option>
              {tournament.divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}

          {/* Optional team logo */}
          <div className="rounded-xl p-3" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
            <div className="flex items-center gap-3">
              <div style={{ width: 52, height: 52, borderRadius: 10, border: '1px solid #2A2A2A', overflow: 'hidden', flexShrink: 0, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {logoPreview ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 10, color: '#525252' }}>Logo</span>}
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold" style={{ color: '#fff' }}>Team Logo <span style={{ color: '#525252', fontWeight: 400 }}>(optional)</span></p>
                <label className="block mt-1">
                  <input data-testid="reg-logo-upload" type="file" accept="image/*" onChange={e => uploadLogo(e.target.files?.[0])} className="block w-full text-xs" style={{ color: '#A3A3A3' }} />
                </label>
                {logoUploading && <p className="text-xs mt-1" style={{ color: '#F59E0B' }}>Uploading…</p>}
                {logoThumbKey && !logoUploading && <p className="text-xs mt-1" style={{ color: '#22C55E' }}>✓ Logo attached</p>}
              </div>
            </div>
          </div>

          {fee > 0 && (() => {
            const payAmount = amountChoice === 'deposit50' ? Math.round(fee / 2) : fee;
            return (
            <div className="rounded-xl p-3" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
              <p className="text-sm font-bold mb-2" style={{ color: '#fff' }}>Entry Fee: <span style={{ color: '#22C55E' }}>₱{fee.toLocaleString()}</span></p>
              <div className="flex gap-2 mb-3 flex-wrap">
                {canPayMongo && (
                  <button type="button" data-testid="pay-paymongo" onClick={() => setPayMethod('paymongo')} className="flex-1 min-w-[120px] py-2 rounded-lg text-xs font-bold" style={{ background: payMethod === 'paymongo' ? '#00E676' : '#1A1A1A', color: payMethod === 'paymongo' ? '#0A0A0A' : '#fff', border: '1px solid #2A2A2A' }}>Pay Online (QR Ph / e-wallet)</button>
                )}
                {gcash && (
                  <button type="button" data-testid="pay-gcash" onClick={() => setPayMethod('gcash')} className="flex-1 min-w-[120px] py-2 rounded-lg text-xs font-bold" style={{ background: payMethod === 'gcash' ? RED : '#1A1A1A', color: '#fff', border: '1px solid #2A2A2A' }}>Pay via GCash</button>
                )}
                {canVenue && (
                  <button type="button" data-testid="pay-venue" onClick={() => { setPayMethod('venue'); setAmountChoice('full'); }} className="flex-1 min-w-[120px] py-2 rounded-lg text-xs font-bold" style={{ background: payMethod === 'venue' ? RED : '#1A1A1A', color: '#fff', border: '1px solid #2A2A2A' }}>Pay at Venue</button>
                )}
              </div>
              {(payMethod === 'gcash' || payMethod === 'paymongo') && canDeposit && (
                <div className="flex gap-2 mb-3">
                  <button type="button" data-testid="pay-amount-full" onClick={() => setAmountChoice('full')}
                    className="px-3 py-1.5 rounded-full text-xs font-bold"
                    style={{ background: amountChoice === 'full' ? RED : '#1A1A1A', color: '#fff', border: '1px solid #2A2A2A' }}>
                    Pay 100% (₱{fee.toLocaleString()})
                  </button>
                  <button type="button" data-testid="pay-amount-deposit" onClick={() => setAmountChoice('deposit50')}
                    className="px-3 py-1.5 rounded-full text-xs font-bold"
                    style={{ background: amountChoice === 'deposit50' ? RED : '#1A1A1A', color: '#fff', border: '1px solid #2A2A2A' }}>
                    50% Deposit (₱{Math.round(fee / 2).toLocaleString()})
                  </button>
                </div>
              )}
              {payMethod === 'paymongo' && (
                <p className="text-xs mb-2" style={{ color: '#00E676' }}>
                  ✓ Secure checkout via PayMongo · You'll be redirected after clicking Register{payments?.fee_percent > 0 ? ` (+${payments.fee_percent}% convenience fee)` : ''}
                </p>
              )}
              {payMethod === 'gcash' && (
                <div>
                  {amountChoice === 'deposit50' && (
                    <p data-testid="deposit-hint" className="text-xs mb-2" style={{ color: '#8B5CF6' }}>Send ₱{payAmount.toLocaleString()} now — the remaining ₱{(fee - payAmount).toLocaleString()} is due at the venue.</p>
                  )}
                  {gcash?.qr_url ? (
                    <div className="flex flex-col items-center p-3 rounded-lg mb-2" style={{ background: '#fff' }}>
                      <img src={gcash.qr_url} alt="GCash QR" style={{ width: 160, height: 160, objectFit: 'contain' }} />
                      {gcash.name && <p className="text-xs mt-1" style={{ color: '#0A0A0A' }}>{gcash.name}</p>}
                    </div>
                  ) : <p className="text-xs mb-2" style={{ color: '#F59E0B' }}>Send ₱{payAmount.toLocaleString()} via GCash{gcash?.name ? ` to ${gcash.name}` : ''}.</p>}
                  <label className="block">
                    <span className="text-xs" style={{ color: '#A3A3A3' }}>Upload payment screenshot *</span>
                    <input data-testid="reg-proof-upload" type="file" accept="image/*" onChange={e => uploadProof(e.target.files?.[0])} className="block w-full text-xs mt-1" style={{ color: '#A3A3A3' }} />
                  </label>
                  {uploading && <p className="text-xs mt-1" style={{ color: '#F59E0B' }}>Uploading…</p>}
                  {proofKey && <p className="text-xs mt-1" style={{ color: '#22C55E' }}>✓ Proof attached</p>}
                </div>
              )}
              {payMethod === 'venue' && <p className="text-xs" style={{ color: '#A3A3A3' }}>You'll pay ₱{fee.toLocaleString()} when you arrive. Your spot is reserved on registration.</p>}

              {/* Clear "amount to pay NOW vs LATER" summary */}
              <div className="mt-3 rounded-lg p-3" data-testid="reg-pay-summary"
                style={{ background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.35)' }}>
                {(() => {
                  let payNow = 0; let payLater = 0; let label = '';
                  if (payMethod === 'venue') {
                    payLater = fee; label = 'Settle in full at the venue';
                  } else if (payMethod === 'gcash') {
                    if (amountChoice === 'deposit50') {
                      payNow = Math.round(fee / 2); payLater = fee - payNow;
                      label = 'Send 50% via GCash · balance at venue';
                    } else {
                      payNow = fee; payLater = 0;
                      label = 'Send full amount via GCash';
                    }
                  } else if (payMethod === 'paymongo') {
                    if (amountChoice === 'deposit50') {
                      payNow = Math.round(fee / 2); payLater = fee - payNow;
                      label = 'Pay 50% online now · balance at venue';
                    } else {
                      payNow = fee; payLater = 0;
                      label = 'Pay in full online now';
                    }
                  }
                  return (
                    <>
                      <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: RED }}>You will pay</p>
                      {payNow > 0 && (
                        <p className="text-lg font-black" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
                          ₱{payNow.toLocaleString()} <span className="text-xs" style={{ color: '#A3A3A3' }}>now</span>
                        </p>
                      )}
                      {payLater > 0 && (
                        <p className="text-sm font-bold" style={{ color: '#A3A3A3' }}>
                          + ₱{payLater.toLocaleString()} at the venue
                        </p>
                      )}
                      <p className="text-[11px] mt-1" style={{ color: '#737373' }}>{label}</p>
                    </>
                  );
                })()}
              </div>
            </div>
            );
          })()}

          {err && <p className="text-xs" style={{ color: RED }}>{err}</p>}
          <button data-testid="reg-submit" onClick={submit} disabled={saving || uploading} className="w-full py-2.5 rounded-lg text-sm font-bold uppercase" style={{ background: RED, color: '#fff', opacity: (saving || uploading) ? 0.6 : 1 }}>
            {saving ? 'Registering…' : 'Register'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LiveScore({ m, onView }) {
  return (
    <div onClick={onView ? () => onView(m) : undefined} data-testid={`live-score-${m.match_number}`}
      className="rounded-xl p-3 shrink-0" style={{ background: '#0F0F0F', border: `1px solid ${RED}`, minWidth: 200, cursor: onView ? 'pointer' : 'default' }}>
      <div className="flex items-center gap-1.5 mb-2">
        <Radio className="h-3 w-3 animate-pulse" style={{ color: RED }} />
        <span className="text-xs font-bold" style={{ color: RED }}>LIVE{m.court_name ? ` · ${m.court_name}` : ''}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold truncate" style={{ color: '#fff', maxWidth: 130 }}>{m.team_a_name}</span>
        <span className="text-lg font-black tabular-nums" style={{ color: RED }}>{m.score_a}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold truncate" style={{ color: '#fff', maxWidth: 130 }}>{m.team_b_name}</span>
        <span className="text-lg font-black tabular-nums" style={{ color: BLUE }}>{m.score_b}</span>
      </div>
      <p className="text-xs mt-1" style={{ color: '#525252' }}>Games {m.games_won_a}–{m.games_won_b} · Game {m.current_game}</p>
    </div>
  );
}

function MatchRow({ m, onView }) {
  const done = m.status === 'completed';
  const viewable = onView && (done || m.status === 'live' || m.status === 'bye');
  const loserColor = (side) => (done && m.winner_side && m.winner_side !== side) ? '#EF4444' : (m.winner_side === side ? '#22C55E' : '#fff');
  return (
    <div onClick={viewable ? () => onView(m) : undefined} data-testid={`match-row-${m.match_number}`}
      className="rounded-lg p-3" style={{ background: '#0F0F0F', border: `1px solid ${m.status === 'live' ? RED : '#2A2A2A'}`, cursor: viewable ? 'pointer' : 'default' }}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold" style={{ color: loserColor('a'), textDecoration: (done && m.winner_side === 'b') ? 'line-through' : 'none' }}>
          {m.winner_side === 'a' && <Crown className="inline h-3 w-3 mr-1" style={{ color: '#22C55E' }} />}{m.team_a_name || 'TBD'}
        </span>
        <span className="text-sm font-bold tabular-nums" style={{ color: '#fff' }}>{done ? m.games_won_a : (m.status === 'live' ? m.score_a : '')}</span>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-sm font-semibold" style={{ color: loserColor('b'), textDecoration: (done && m.winner_side === 'a') ? 'line-through' : 'none' }}>
          {m.winner_side === 'b' && <Crown className="inline h-3 w-3 mr-1" style={{ color: '#22C55E' }} />}{m.team_b_name || 'TBD'}
        </span>
        <span className="text-sm font-bold tabular-nums" style={{ color: '#fff' }}>{done ? m.games_won_b : (m.status === 'live' ? m.score_b : '')}</span>
      </div>
      {m.status === 'live' && <p className="text-xs mt-1" style={{ color: RED }}>● LIVE{m.court_name ? ` · ${m.court_name}` : ''}</p>}
      {m.status === 'bye' && <p className="text-xs mt-1" style={{ color: '#525252' }}>BYE</p>}
      {viewable && done && <p className="text-[10px] mt-1 font-bold uppercase" style={{ color: '#525252' }}>View match history →</p>}
    </div>
  );
}

function champOf(format, matches) {
  const fin = matches.filter(m => (m.bracket_type === 'final_reset' || m.bracket_type === 'final') && m.status === 'completed').sort((a, b) => b.round - a.round)[0]
    || (format === 'single_elimination' ? (() => {
        const nonThird = matches.filter(m => m.bracket_type !== 'third_place' && m.bracket_type !== 'losers');
        if (!nonThird.length) return null;
        const maxRound = Math.max(...nonThird.map(m => m.round || 0));
        return nonThird.filter(m => (m.round || 0) === maxRound && m.status === 'completed' && m.winner_side)[0] || null;
      })() : null);
  if (!fin || !fin.winner_side) return null;
  return fin.winner_side === 'a' ? fin.team_a_name : fin.team_b_name;
}

function PublicBracketView({ matches, champion, onView, logoMap, onLogo }) {
  const wb = matches.filter(m => m.bracket_type === 'winners');
  const lb = matches.filter(m => m.bracket_type === 'losers');
  const finals = matches.filter(m => m.bracket_type === 'final' || (m.bracket_type === 'final_reset' && m.status !== 'cancelled'));
  const dropMap = buildDropMap(matches);
  return (
    <div className="space-y-6">
      <div>
        <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest mb-2 font-bold" style={{ color: '#34D399' }}><GitBranch className="h-3.5 w-3.5" style={{ flex: 'none' }} />Winners Bracket</p>
        <TournamentBracket rounds={groupRounds(wb)} showChampion={false} onView={onView} logoMap={logoMap} onLogo={onLogo} dropMap={dropMap} />
      </div>
      {lb.length > 0 && (
        <div>
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest mb-2 font-bold" style={{ color: '#F59E0B' }}><ArrowDown className="h-3.5 w-3.5" style={{ flex: 'none' }} />Losers Bracket</p>
          <ColumnsView matches={lb} onView={onView} logoMap={logoMap} onLogo={onLogo} />
        </div>
      )}
      {finals.length > 0 && (
        <div>
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest mb-2 font-bold" style={{ color: RED }}><Trophy className="h-3.5 w-3.5" style={{ flex: 'none' }} />Grand Final{champion ? ` · Champion: ${champion}` : ''}</p>
          <div className="flex gap-2 items-start rounded-lg mb-3" style={{ background: '#12100A', border: '1px solid #2A2410', padding: '9px 12px', maxWidth: 460 }}>
            <Info className="h-3.5 w-3.5 mt-0.5" style={{ color: '#F59E0B', flex: 'none' }} />
            <p className="text-xs" style={{ color: '#C9B98A', lineHeight: 1.5 }}>
              The <b style={{ color: '#FFD54A', fontWeight: 700 }}>winners-bracket</b> side needs one win; the <b style={{ color: '#FFD54A', fontWeight: 700 }}>losers-bracket</b> side must win twice — taking Grand Final 1 forces a <b style={{ color: '#FFD54A', fontWeight: 700 }}>bracket reset</b> (one deciding match, both teams at 1 loss).
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3" style={{ maxWidth: 460 }}>
            {finals.map(m => (
              <div key={m.id}>
                <p className="text-[9px] font-bold uppercase mb-1" style={{ letterSpacing: '0.1em', color: m.bracket_type === 'final_reset' ? '#FF6B78' : '#8A93A3' }}>
                  {m.bracket_type === 'final_reset' ? 'Bracket reset · winner-take-all' : 'Grand Final'}
                </p>
                <MatchRow m={m} onView={onView} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Renders one division's bracket on the public page.
function PublicDivisionBracket({ format, matches, onView, logoMap, onLogo }) {
  const champion = champOf(format, matches);
  if (format === 'double_elimination') {
    return <PublicBracketView matches={matches} champion={champion} onView={onView} logoMap={logoMap} onLogo={onLogo} />;
  }
  const bronze = matches.find(m => m.bracket_type === 'third_place');
  return (
    <TournamentBracket rounds={groupRounds(matches.filter(m => m.bracket_type !== 'third_place'))}
      champion={champion} bronzeMatch={bronze || null} onView={onView} logoMap={logoMap} onLogo={onLogo} />
  );
}

export default function SportsPublicTournamentPage() {
  const { slug, tid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [live, setLive] = useState([]);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('bracket');
  const [bview, setBview] = useState('bracket');
  const [showReg, setShowReg] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { lightboxUrl, openLightbox, closeLightbox } = useLogoLightbox();

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/sports/public/${slug}/tournaments/${tid}`); setData(r.data); }
    catch (e) { setError(errText(e, 'Tournament not found')); }
  }, [slug, tid]);

  const loadLive = useCallback(async () => {
    try { const r = await axios.get(`${API}/sports/public/${slug}/tournaments/${tid}/live`); setLive(r.data.live || []); }
    catch { /* ignore */ }
  }, [slug, tid]);

  const openHistory = useCallback((m) => { navigate(`/book/${slug}/match/${m.id}`); }, [navigate, slug]);

  useEffect(() => { load(); loadLive(); }, [load, loadLive]);
  useEffect(() => {
    const announce = data?.tournament?.event_style === 'announce';
    const iv = setInterval(() => {
      if (announce) { load(); }         // no matches can ever exist — skip /live
      else { loadLive(); load(); }
    }, announce ? 60000 : 5000);
    return () => clearInterval(iv);
  }, [loadLive, load, data?.tournament?.event_style]);

  // Per-event browser tab title
  useEffect(() => {
    const tn = data?.tournament?.name;
    const fac = data?.facility?.name;
    if (tn) document.title = fac ? `${tn} — ${fac}` : tn;
    return () => { document.title = 'AgriBooks | Smart Business Management System'; };
  }, [data]);

  if (error) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A', color: '#fff' }}>{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A', color: '#737373' }}>Loading…</div>;

  const t = data.tournament;
  // Tenant theme (sports-page-design): the shell follows the facility's preset
  // instead of the legacy hardcoded palette. Shadowing RED re-tints every
  // accent in this component; sub-components keep their own styling for now.
  const T = THEMES[data.facility?.theme_preset] || THEMES['Velocity Red'];
  const RED = T.primary;
  const canRegister = t.status === 'registration_open';
  const logoMap = buildLogoMap(data.teams);

  return (
    <div className="min-h-screen" style={{ background: `radial-gradient(900px 480px at 88% -8%, ${T.primary}2E, transparent 62%), radial-gradient(700px 420px at -12% 34%, ${T.primary}1A, transparent 58%), radial-gradient(560px 380px at 55% 108%, ${T.primary}14, transparent 60%), ${T.bg}` }}>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button onClick={() => navigate(`/book/${slug}`)} className="flex items-center gap-2 text-sm mb-4" style={{ color: '#737373' }}>
          <ArrowLeft className="h-4 w-4" />
          {data.facility.logo_url && (
            <img src={data.facility.logo_url} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }} />
          )}
          {data.facility.name}
        </button>

        {/* Header */}
        <div className="rounded-2xl mb-5 overflow-hidden" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          {/* Facility branding strip */}
          {data.facility.logo_url && (
            <div className="flex items-center gap-3 px-5 pt-4 pb-3" style={{ borderBottom: '1px solid #1E1E1E' }}>
              <img src={data.facility.logo_url} alt={data.facility.name} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#525252', letterSpacing: '0.12em' }}>{data.facility.name}</span>
            </div>
          )}
          {/* Poster hero — diagonal cut, oversized display type, layered accent
              shapes (magazine energy without stock photography). */}
          <div className="relative overflow-hidden" style={{ background: T.gradient, clipPath: 'polygon(0 0, 100% 0, 100% 84%, 0 100%)', padding: '30px 24px 64px' }}>
            {/* Ghost giant text + diagonal sheen for depth */}
            <span aria-hidden style={{ position: 'absolute', right: -18, top: -26, fontFamily: 'Oswald, sans-serif', fontWeight: 900, fontSize: 150, lineHeight: 1, color: '#ffffff12', letterSpacing: '-0.02em', userSelect: 'none', whiteSpace: 'nowrap' }}>
              {(t.date_start || '').slice(0, 4) || 'PLAY'}
            </span>
            <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(115deg, #00000038 0%, transparent 45%, #ffffff14 100%)' }} />
            <div className="relative">
              <p className="text-xs font-bold uppercase" style={{ color: '#FFFFFFB8', letterSpacing: '0.3em', marginBottom: 8 }}>
                {t.date_start ? fmtDate(t.date_start) : 'Tournament'}{t.date_end && t.date_end !== t.date_start ? ` – ${fmtDate(t.date_end)}` : ''}
              </p>
              <h1 className="font-black" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', fontSize: 'clamp(32px, 6vw, 54px)', lineHeight: 0.98, letterSpacing: '0.01em', textShadow: '0 2px 24px #00000040' }}>
                {t.name.toUpperCase()}
              </h1>
              {t.description && <p className="text-sm mt-3 max-w-xl" style={{ color: '#FFFFFFD9', lineHeight: 1.55 }}>{t.description}</p>}
              {t.entry_fee > 0 && (
                <p className="mt-3 inline-block text-sm font-black px-3 py-1.5 rounded-lg" style={{ background: '#00000030', color: '#fff', border: '1px solid #ffffff30' }}>
                  ₱{Number(t.entry_fee).toLocaleString()} <span style={{ fontWeight: 600, opacity: 0.8 }}>entry</span>
                </p>
              )}
            </div>
          </div>
          <div className="p-5" style={{ paddingTop: 14 }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                {/* Meta pills */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {/* Format/mode pills describe the bracket engine — meaningless for announce-style events */}
                  {t.event_style !== 'announce' && (
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: T.bg, color: T.muted, border: `1px solid ${T.border}` }}>
                      {t.format === 'round_robin' ? 'Round Robin' : t.format === 'double_elimination' ? 'Double Elimination' : 'Single Elimination'}
                    </span>
                  )}
                  {t.event_style !== 'announce' && (
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: T.bg, color: T.muted, border: `1px solid ${T.border}` }}>
                      {t.game_mode === 'custom' ? `${t.team_size}v${t.team_size}` : t.game_mode === 'singles' ? 'Singles' : 'Doubles'}
                    </span>
                  )}
                  {(t.event_style !== 'announce' || t.announce_registration === 'onsite') && (
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1" style={{ background: T.bg, color: T.muted, border: `1px solid ${T.border}` }}>
                      <Users className="h-3 w-3" /> {data.teams.length} team{data.teams.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {/* Date + fee moved into the poster hero — no duplicates here */}
                </div>
                {(t.contact_email || t.contact_phone) && (
                  <p data-testid="public-contact" className="text-xs mt-3" style={{ color: '#525252' }}>
                    Questions? Contact {t.contact_name ? <strong style={{ color: '#737373' }}>{t.contact_name}</strong> : 'the organizer'}
                    {t.contact_email && <> · <a href={`mailto:${t.contact_email}`} style={{ color: RED }}>{t.contact_email}</a></>}
                    {t.contact_phone && <> · {t.contact_phone}</>}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {/* Announce + external: Register goes straight to the external site (Reclub etc.) */}
                {canRegister && t.event_style === 'announce' && t.announce_registration === 'external' && t.registration_url && (
                  <a data-testid="public-register-ext" href={t.registration_url} target="_blank" rel="noopener noreferrer"
                    className="px-5 py-3 text-sm font-black uppercase tracking-wider text-center"
                    style={{ background: T.gradient, color: '#fff', letterSpacing: '0.08em', boxShadow: `0 0 24px ${T.glow}`, textDecoration: 'none', borderRadius: 999 }}>
                    Register ↗
                  </a>
                )}
                {canRegister && (t.event_style !== 'announce' || t.announce_registration === 'onsite') && (
                  <button data-testid="public-register-btn" onClick={() => setShowReg(true)}
                    className="px-5 py-3 text-sm font-black uppercase tracking-wider"
                    style={{ background: T.gradient, color: '#fff', letterSpacing: '0.08em', boxShadow: `0 0 24px ${T.glow}`, borderRadius: 999 }}>
                    Register Team
                  </button>
                )}
                {t.results_url && (
                  <a data-testid="public-results-link" href={t.results_url} target="_blank" rel="noopener noreferrer"
                    className="px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-center"
                    style={{ background: `${T.primary}14`, color: T.text, border: `1px solid ${T.primary}55`, textDecoration: 'none', borderRadius: 999 }}>
                    View Results ↗
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Awards & prizes — announce-style events. Prizes are public from the
            announcement; winners appear once the organizer declares them. */}
        {t.event_style === 'announce' && (t.awards || []).length > 0 && (
          <div className="mb-5 grid gap-4" style={{ gridTemplateColumns: (t.awards.length > 1) ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr' }} data-testid="public-awards">
            {t.awards.map((g, gi) => {
              const dName = (t.divisions || []).find(d => d.id === g.division_id)?.name;
              const MEDALS = ['#FFD700', '#C0C0C0', '#CD7F32'];
              return (
                <div key={gi} className="rounded-2xl p-5" style={{ background: `linear-gradient(150deg, ${T.primary}1C 0%, ${T.surface} 52%)`, border: `1px solid ${T.primary}30`, boxShadow: `0 12px 40px -18px ${T.glow}` }}>
                  <p className="text-sm font-black uppercase mb-3" style={{ fontFamily: 'Oswald, sans-serif', color: T.text, letterSpacing: '0.06em' }}>
                    <Trophy className="h-4 w-4 inline mr-1.5" style={{ color: RED, verticalAlign: '-2px' }} />
                    {dName ? <>{dName} <span style={{ color: T.primary }}>AWARDS</span></> : <>PRIZES & <span style={{ color: T.primary }}>AWARDS</span></>}
                  </p>
                  {(g.main || []).map((a, i) => (
                    <div key={`m-${i}`} className="flex items-start gap-3 py-2" style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                      <span className="text-xs font-black shrink-0 mt-0.5 px-3 py-1 inline-block"
                        style={{ background: `linear-gradient(120deg, ${(MEDALS[i] || T.primary)}30, ${(MEDALS[i] || T.primary)}12)`, color: MEDALS[i] || T.muted, border: `1px solid ${(MEDALS[i] || T.primary)}45`, transform: 'skewX(-10deg)', borderRadius: 6 }}>
                        <span style={{ display: 'inline-block', transform: 'skewX(10deg)' }}>{a.label}</span></span>
                      <div className="min-w-0">
                        {a.prize && <p className="text-sm font-semibold" style={{ color: '#22C55E' }}>{a.prize}</p>}
                        {a.recipient
                          ? <p className="text-sm font-black" style={{ color: T.text }}>{a.recipient}</p>
                          : <p className="text-xs" style={{ color: T.subtle }}>To be decided</p>}
                      </div>
                    </div>
                  ))}
                  {(g.sub || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${T.border}` }}>
                      {g.sub.map((a, i) => (
                        <span key={`s-${i}`} className="text-xs px-2.5 py-1 rounded-full" style={{ background: `linear-gradient(120deg, ${T.primary}1E, ${T.bg})`, border: `1px solid ${T.primary}35`, color: T.muted }}>
                          <strong style={{ color: T.text }}>{a.label}</strong>
                          {a.prize && <> · <span style={{ color: '#22C55E' }}>{a.prize}</span></>}
                          {a.recipient && <> — <strong style={{ color: RED }}>{a.recipient}</strong></>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Live band */}
        {live.length > 0 && (
          <div className="mb-5">
            <p className="text-xs uppercase tracking-wide mb-2 font-bold flex items-center gap-1.5" style={{ color: RED }}><Radio className="h-3.5 w-3.5 animate-pulse" /> Live Now</p>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {live.map(m => <LiveScore key={m.id} m={m} onView={openHistory} />)}
            </div>
          </div>
        )}

        {/* Tabs */}
        {data.matches.length > 0 && (
          <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A', width: 'fit-content' }}>
            <button onClick={() => setTab('bracket')} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: tab === 'bracket' ? RED : 'transparent', color: tab === 'bracket' ? '#fff' : '#737373' }}>
              <GitBranch className="h-4 w-4" /> {t.format === 'round_robin' ? 'Matches' : 'Bracket'}
            </button>
            {t.format === 'round_robin' && (
              <button onClick={() => setTab('standings')} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: tab === 'standings' ? RED : 'transparent', color: tab === 'standings' ? '#fff' : '#737373' }}>
                <BarChart3 className="h-4 w-4" /> Standings
              </button>
            )}
          </div>
        )}

        {/* Champions podium(s) — one per decided division */}
        {data.matches.length > 0 && (() => {
          const groups = groupByDivision(data.matches, t.divisions);
          const podiums = groups
            .map(g => ({ divName: g.divName, podium: computePodium(t.format, g.matches, data.standings?.[g.divId]) }))
            .filter(p => p.podium);
          if (!podiums.length) return null;
          return (
            <div className="mb-6 grid gap-4" style={{ gridTemplateColumns: podiums.length > 1 ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr' }}>
              {podiums.map(p => <DivisionPodium key={p.divName || 'all'} divName={p.divName} podium={p.podium} logoMap={logoMap} onLogo={openLightbox} teams={data.teams || []}
                cardMeta={{ eventName: t.name, facilityName: data.facility?.name, date: t.date_start }} />)}
            </div>
          );
        })()}

        {/* No bracket yet — show team roster cards, grouped by division */}
        {data.matches.length === 0 && (
          <div>
            {/* Division quick-nav (only when multiple divisions have teams) */}
            {t.divisions?.length >= 2 && data.teams.length > 0 && (() => {
              const divGroups = t.divisions.map(d => ({
                id: d.id, name: d.name,
                count: data.teams.filter(tm => tm.division_id === d.id).length,
              })).filter(g => g.count > 0);
              if (divGroups.length < 2) return null;
              return (
                <div className="flex flex-wrap gap-2 mb-5">
                  {divGroups.map(g => (
                    <button key={g.id}
                      className="px-3 py-1.5 rounded-full text-xs font-bold transition-colors"
                      style={{ background: '#1A0A0C', color: RED, border: `1px solid ${RED}40`, cursor: 'pointer' }}
                      onClick={() => document.getElementById(`div-${g.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                      {g.name} <span style={{ opacity: 0.7 }}>({g.count})</span>
                    </button>
                  ))}
                </div>
              );
            })()}

            {canRegister && data.teams.length === 0 && (
              <div className="rounded-2xl p-10 text-center mb-5" style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}>
                <Trophy className="h-8 w-8 mx-auto mb-3" style={{ color: '#2A2A2A' }} />
                <p className="text-sm" style={{ color: '#A3A3A3' }}>
                  {t.event_style === 'announce' && t.announce_registration === 'external'
                    ? 'Registration is open — tap Register to sign up.'
                    : t.event_style === 'announce' && t.announce_registration === 'none'
                      ? 'See you at the event — details above.'
                      : 'Registration is open — be the first to sign up your team!'}
                </p>
              </div>
            )}
            {!canRegister && data.teams.length === 0 && (
              <div className="rounded-2xl p-10 text-center mb-5" style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}>
                <p className="text-sm" style={{ color: '#A3A3A3' }}>
                  {t.event_style === 'announce'
                    ? (t.results_url ? 'Event details above — results are posted via the View Results link.' : 'Event details above — winners are posted here after the event.')
                    : 'The bracket has not been released yet. Check back soon.'}
                </p>
              </div>
            )}

            {data.teams.length > 0 && (() => {
              const hasDivs = t.divisions?.length > 0;
              const divGroups = hasDivs
                ? t.divisions.map(d => ({
                    id: d.id, name: d.name, maxTeams: d.max_teams,
                    teams: data.teams.filter(tm => tm.division_id === d.id),
                  })).filter(g => g.teams.length > 0)
                : [{ id: null, name: null, maxTeams: null, teams: data.teams }];
              if (hasDivs) {
                const assignedIds = new Set(t.divisions.map(d => d.id));
                const orphans = data.teams.filter(tm => !tm.division_id || !assignedIds.has(tm.division_id));
                if (orphans.length) divGroups.push({ id: 'other', name: 'Other', maxTeams: null, teams: orphans });
              }
              return (
                <div className="space-y-7">
                  {divGroups.map((grp, gi) => {
                    let globalOffset = 0;
                    for (let i = 0; i < gi; i++) globalOffset += divGroups[i].teams.length;
                    return (
                      <div key={grp.id || 'all'} id={grp.id && grp.id !== 'other' ? `div-${grp.id}` : undefined}>
                        {grp.name ? (
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-1 h-5 rounded-full" style={{ background: RED }} />
                              <h3 className="text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: '0.08em' }}>{grp.name}</h3>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold" style={{ color: '#525252' }}>
                                {grp.teams.length}{grp.maxTeams ? ` / ${grp.maxTeams}` : ''} team{grp.teams.length !== 1 ? 's' : ''}
                              </span>
                              {grp.maxTeams && grp.teams.length >= grp.maxTeams && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase" style={{ background: '#1A0A0C', color: RED, border: `1px solid ${RED}30` }}>FULL</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#525252', letterSpacing: '0.12em' }}>
                              {canRegister ? 'Teams Registered' : `${data.teams.length} Competitor${data.teams.length !== 1 ? 's' : ''}`}
                            </p>
                            <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: '#1A0A0C', color: RED, border: `1px solid ${RED}30` }}>
                              {data.teams.length} team{data.teams.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                          {grp.teams.map((tm, idx) => (
                            <div key={tm.id} data-testid={`team-card-${tm.id}`}
                              className="rounded-xl p-3.5 flex gap-3 items-start"
                              style={{ background: '#0F0F0F', border: '1px solid #1E1E1E' }}>
                              <div className="shrink-0" style={{ width: 44, height: 44 }}>
                                {tm.logo_url ? (
                                  <img src={tm.logo_url} alt={tm.team_name}
                                    style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', border: '1px solid #2A2A2A' }} />
                                ) : (
                                  <div className="flex items-center justify-center rounded-xl font-black text-base"
                                    style={{ width: 44, height: 44, background: '#1A0A0C', color: RED, border: `1px solid ${RED}30`, fontFamily: 'Oswald, sans-serif' }}>
                                    {globalOffset + idx + 1}
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-black text-sm leading-tight truncate" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: '0.03em' }}>
                                  {(tm.team_name || '').toUpperCase()}
                                </p>
                                {tm.players?.length > 0 && (
                                  <div className="mt-1 space-y-0.5">
                                    {tm.players.map((p, pi) => (
                                      <p key={pi} className="text-xs truncate" style={{ color: '#737373' }}>{p.name}</p>
                                    ))}
                                  </div>
                                )}
                                {tm.status === 'confirmed' && (
                                  <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide" style={{ background: '#0D2B1A', color: '#22C55E' }}>Confirmed</span>
                                )}
                                {tm.status === 'pending' && (
                                  <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide" style={{ background: '#1A1500', color: '#F59E0B' }}>Pending</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {canRegister && (t.event_style !== 'announce' || t.announce_registration === 'onsite') && (
                    <p className="text-xs text-center" style={{ color: '#525252' }}>
                      Spots are filling up — <button onClick={() => setShowReg(true)} style={{ color: RED, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 700 }}>register your team now</button>
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Bracket / Matches */}
        {tab === 'bracket' && data.matches.length > 0 && (
          <div>
            {t.format !== 'round_robin' && (
              <div className="flex gap-1 mb-4 p-1 rounded-lg" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A', width: 'fit-content' }}>
                {[['bracket', 'Bracket', GitBranch], ['list', 'List', List]].map(([k, l, Ic]) => (
                  <button key={k} data-testid={`pub-bview-${k}`} onClick={() => setBview(k)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"
                    style={{ background: bview === k ? RED : 'transparent', color: bview === k ? '#fff' : '#737373' }}>
                    <Ic className="h-3.5 w-3.5" /> {l}
                  </button>
                ))}
              </div>
            )}
            {(bview === 'list' || t.format === 'round_robin') ? (
              groupByDivision(data.matches, t.divisions).map(g => (
                <div key={g.divId || 'all'} className="mb-6" data-testid={`pub-division-${g.divId || 'all'}`}>
                  {g.divName && <p className="text-sm font-bold uppercase mb-2 px-2 py-1 rounded-lg inline-block" style={{ background: '#E6394618', color: RED, fontFamily: 'Oswald, sans-serif' }}>{g.divName}</p>}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                    <div className="lg:col-span-2 min-w-0">
                      <MatchListView matches={g.matches} onView={openHistory} logoMap={logoMap} onLogo={openLightbox} />
                    </div>
                    <div className="lg:col-span-1">
                      <RankingPanel format={t.format} matches={g.matches} standings={data.standings?.[g.divId]} logoMap={logoMap} onLogo={openLightbox} />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              groupByDivision(data.matches, t.divisions).map(g => (
                <div key={g.divId || 'all'} className="mb-8" data-testid={`pub-division-${g.divId || 'all'}`}>
                  {g.divName && <p className="text-sm font-bold uppercase mb-3 px-2 py-1 rounded-lg inline-block" style={{ background: '#E6394618', color: RED, fontFamily: 'Oswald, sans-serif' }}>{g.divName}</p>}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                    <div className="lg:col-span-2 min-w-0">
                      <PublicDivisionBracket format={t.format} matches={g.matches} onView={openHistory} logoMap={logoMap} onLogo={openLightbox} />
                    </div>
                    <div className="lg:col-span-1">
                      <RankingPanel format={t.format} matches={g.matches} standings={data.standings?.[g.divId]} logoMap={logoMap} onLogo={openLightbox} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Standings */}
        {tab === 'standings' && (
          <div className="space-y-6">
            {Object.entries(data.standings).map(([divId, rows]) => (
              <div key={divId} className="rounded-xl overflow-hidden" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
                {divId !== 'null' && t.divisions?.find(d => d.id === divId) && (
                  <p className="px-4 py-2 text-xs uppercase font-bold" style={{ color: RED, borderBottom: '1px solid #2A2A2A' }}>{t.divisions.find(d => d.id === divId).name}</p>
                )}
                <StandingsTable rows={rows} matches={data.matches} logoMap={logoMap} onLogo={openLightbox} />
              </div>
            ))}
          </div>
        )}
      </div>
      <LogoLightbox url={lightboxUrl} onClose={closeLightbox} />

      {showReg && <RegisterModal slug={slug} tid={tid} tournament={t} gcash={data.gcash} payments={data.payments} onClose={() => setShowReg(false)} onDone={() => { setShowReg(false); setRegistered(true); load(); }} />}
      {registered && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)' }} onClick={() => setRegistered(false)}>
          <div className="text-center p-6 rounded-2xl" style={{ background: '#0F0F0F', border: '1px solid #22C55E' }}>
            <Trophy className="h-10 w-10 mx-auto mb-2" style={{ color: '#22C55E' }} />
            <p className="text-lg font-bold" style={{ color: '#fff' }}>You're registered!</p>
            <p className="text-sm mt-1" style={{ color: '#A3A3A3' }}>See you at the courts. Good luck!</p>
            <button onClick={() => setRegistered(false)} className="mt-4 px-5 py-2 rounded-lg text-sm font-bold" style={{ background: RED, color: '#fff' }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
